import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adCampaignService, AdCampaignQueryParams } from '@/services/adCampaignService';
import { AdCampaignDisplayNowRequest, AdCampaignStatus, CreateAdCampaignRequest } from '@/types/ads';

export const adCampaignKeys = {
  all: ['adCampaigns'] as const,
  lists: () => [...adCampaignKeys.all, 'list'] as const,
  list: (params: AdCampaignQueryParams) => [...adCampaignKeys.lists(), params] as const,
  details: () => [...adCampaignKeys.all, 'detail'] as const,
  detail: (id: string) => [...adCampaignKeys.details(), id] as const,
  preview: () => [...adCampaignKeys.all, 'preview'] as const,
};

export const useAdCampaigns = (params: AdCampaignQueryParams = {}) => {
  const queryResult = useQuery({
    queryKey: adCampaignKeys.list(params),
    queryFn: () => adCampaignService.getAdCampaigns(params),
    staleTime: 30 * 1000,
  });

  return {
    data: queryResult.data,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
};

export const useAdCampaign = (id: string) => {
  return useQuery({
    queryKey: adCampaignKeys.detail(id),
    queryFn: () => adCampaignService.getAdCampaignById(id),
    enabled: !!id,
  });
};

export const useCreateAdCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAdCampaignRequest) => adCampaignService.createAdCampaign(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adCampaignKeys.lists() });
    },
  });
};

export const useUpdateAdCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateAdCampaignRequest> & { version: number } }) =>
      adCampaignService.updateAdCampaign(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: adCampaignKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: adCampaignKeys.lists() });
    },
  });
};

export const useUpdateAdCampaignStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, version }: { id: string; status: AdCampaignStatus; version: number }) =>
      adCampaignService.updateAdCampaignStatus(id, status, version),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: adCampaignKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: adCampaignKeys.lists() });
    },
  });
};

export const useDisplayAdCampaignNow = () => {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AdCampaignDisplayNowRequest }) =>
      adCampaignService.displayNow(id, data),
  });
};

export const useDeleteAdCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adCampaignService.deleteAdCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adCampaignKeys.lists() });
    },
  });
};
